import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@kognitos/lattice';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models';
import { useDeactivateTrigger } from '@/generated/hooks/grimoire/v1/automation_phase';
import { captureException } from '@/modules/sentry';
import {
  TRIGGER_TYPE,
  type TTriggerType,
} from '../../utils/trigger-status-utils';

/**
 * Props for the PauseTriggerDialog component.
 */
export interface IPauseTriggerDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The type of trigger being paused */
  triggerType: TTriggerType;
  /** The organization ID */
  organizationId: string;
  /** The workspace ID */
  workspaceId: string;
  /** The automation ID */
  automationId: string;
  /** The trigger ID to pause */
  triggerId: string;
  /** Whether the automation is a draft */
  isDraft?: boolean;
  /** Callback when pause is successful */
  onSuccess?: () => void;
}

/**
 * A confirmation dialog for pausing triggers.
 *
 * Displays trigger-type-specific messaging (webhook vs email) and handles
 * the pause mutation with loading states, toast notifications, and error handling.
 *
 * @param {IPauseTriggerDialogProps} props - Component props
 * @returns {JSX.Element} The pause trigger confirmation dialog
 *
 * @example
 * <PauseTriggerDialog
 *   isOpen={isPauseDialogOpen}
 *   onClose={closePauseDialog}
 *   triggerType="webhook"
 *   organizationId={orgId}
 *   workspaceId={wsId}
 *   automationId={autoId}
 *   triggerId={triggerId}
 *   onSuccess={handlePauseSuccess}
 * />
 */
export function PauseTriggerDialog({
  isOpen,
  onClose,
  triggerType,
  organizationId,
  workspaceId,
  automationId,
  triggerId,
  isDraft = false,
  onSuccess,
}: IPauseTriggerDialogProps) {
  const { t } = useTranslation('automation-inputs');

  const { mutate: pauseTrigger, isPending } = useDeactivateTrigger({
    mutation: {
      onSuccess: () => {
        toast.success(t('trigger.toast.pauseSuccess'));
        onClose();
        onSuccess?.();
      },
      onError: error => {
        toast.error(t('trigger.toast.pauseError'));
        captureException(error, {
          tags: {
            component: 'PauseTriggerDialog',
            action: 'pauseTrigger',
          },
        });
      },
    },
  });

  /**
   * Handles the pause trigger action when user confirms.
   */
  const handlePauseTrigger = () => {
    pauseTrigger({
      organization: organizationId,
      workspace: workspaceId,
      automation: automationId,
      triggerId,
      data: {
        stage: isDraft
          ? V1AutomationStage.AUTOMATION_STAGE_DRAFT
          : V1AutomationStage.AUTOMATION_STAGE_PUBLISHED,
      },
    });
  };

  const titleKey =
    triggerType === TRIGGER_TYPE.WEBHOOK
      ? 'pauseDialog.webhookTitle'
      : triggerType === TRIGGER_TYPE.EMAIL
        ? 'pauseDialog.emailTitle'
        : 'pauseDialog.pollTitle';
  const descriptionKey =
    triggerType === TRIGGER_TYPE.WEBHOOK
      ? 'pauseDialog.webhookDescription'
      : triggerType === TRIGGER_TYPE.EMAIL
        ? 'pauseDialog.emailDescription'
        : 'pauseDialog.pollDescription';

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription className="flex flex-col">
            <span>{t(descriptionKey)}</span>
            <span>{t('pauseDialog.continueNote')}</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('pauseDialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handlePauseTrigger}
            disabled={isPending}
            isLoading={isPending}
          >
            {t('pauseDialog.pauseTrigger')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
